import React, { useCallback, useEffect, useRef, useState } from "react";
import { logix2Dock } from "@/api/adminOneClient";

/**
 * CareLogix™ ORACLE — LOGIX-2™ Mic Button (Phase 20)
 *
 * Push-to-talk microphone input. Defaults to browser Web Speech API
 * (free, on-device). If unavailable, falls back to MediaRecorder →
 * POST /api/logix2/voice/transcribe which uses OpenAI Whisper when
 * `OPENAI_API_KEY` is configured server-side.
 *
 * Props:
 *   - onResult(text) — called when transcription succeeds
 *   - onState(state) — "idle" | "recording" | "transcribing" | "error"
 */
const BROWSER_STT_OK = typeof window !== "undefined" && (
  "webkitSpeechRecognition" in window || "SpeechRecognition" in window
);

const MEDIA_REC_OK = typeof window !== "undefined" && "MediaRecorder" in window && navigator?.mediaDevices?.getUserMedia;

export default function Logix2MicButton({ onResult, onState, className = "", title = "Voice input" } = {}) {
  const [state, setState] = useState("idle"); // idle | recording | transcribing | error
  const [error, setError] = useState("");
  const recogRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  useEffect(() => { onState && onState(state); }, [state, onState]);
  useEffect(() => () => cleanupAll(), []); // eslint-disable-line react-hooks/exhaustive-deps

  const cleanupAll = () => {
    try { recogRef.current && recogRef.current.stop(); } catch { /* ignore */ }
    try { recorderRef.current && recorderRef.current.state === "recording" && recorderRef.current.stop(); } catch { /* ignore */ }
    try { streamRef.current && streamRef.current.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
    recogRef.current = null;
    recorderRef.current = null;
    streamRef.current = null;
    chunksRef.current = [];
  };

  const startBrowser = useCallback(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recog = new SR();
    recog.continuous = false;
    recog.interimResults = false;
    recog.lang = (typeof navigator !== "undefined" && navigator.language) || "en-US";
    recog.onstart = () => setState("recording");
    recog.onresult = (e) => {
      const txt = (e?.results?.[0]?.[0]?.transcript || "").trim();
      if (txt) onResult && onResult(txt);
      setState("idle");
    };
    recog.onerror = (e) => {
      setError(e?.error || "mic_error");
      setState("error");
    };
    recog.onend = () => {
      if (state === "recording") setState("idle");
    };
    try {
      recog.start();
      recogRef.current = recog;
    } catch (e) {
      setError(e?.message || "mic_start_failed");
      setState("error");
    }
  }, [onResult, state]);

  const startMediaRecorder = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mr = new MediaRecorder(stream);
      recorderRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstart = () => setState("recording");
      mr.onstop = async () => {
        setState("transcribing");
        const blob = new Blob(chunksRef.current, { type: chunksRef.current[0]?.type || "audio/webm" });
        const fd = new FormData();
        fd.append("audio", blob, "voice.webm");
        try {
          const { data } = await logix2Dock.voiceTranscribe(fd);
          if (data?.text) {
            onResult && onResult(data.text);
            setState("idle");
          } else {
            setError(data?.reason || "transcription_unavailable");
            setState("error");
          }
        } catch (e) {
          setError(e?.response?.data?.detail || e?.message || "transcribe_failed");
          setState("error");
        } finally {
          cleanupAll();
        }
      };
      mr.start();
    } catch (e) {
      const name = e?.name || "";
      if (name === "NotAllowedError" || name === "SecurityError") {
        setError("Microphone permission denied. Allow access to use voice input.");
      } else {
        setError(e?.message || "mic_unavailable");
      }
      setState("error");
    }
  }, [onResult]);

  const start = useCallback(() => {
    setError("");
    if (BROWSER_STT_OK) {
      startBrowser();
    } else if (MEDIA_REC_OK) {
      startMediaRecorder();
    } else {
      setError("Microphone not supported in this browser.");
      setState("error");
    }
  }, [startBrowser, startMediaRecorder]);

  const stop = useCallback(() => {
    try { recogRef.current && recogRef.current.stop(); } catch { /* ignore */ }
    try { recorderRef.current && recorderRef.current.state === "recording" && recorderRef.current.stop(); } catch { /* ignore */ }
  }, []);

  const onClick = () => {
    if (state === "recording") stop(); else start();
  };

  const supported = BROWSER_STT_OK || MEDIA_REC_OK;

  return (
    <button
      type="button"
      className={`l2-icon-btn l2-icon-btn--mic ${state === "recording" ? "is-recording" : ""} ${className}`}
      onClick={onClick}
      disabled={!supported || state === "transcribing"}
      aria-label={state === "recording" ? "Stop recording" : "Start voice input"}
      aria-pressed={state === "recording"}
      title={!supported ? "Mic not supported" : (state === "recording" ? "Tap to stop" : title)}
      data-testid="logix2-mic-button"
    >
      {state === "transcribing" ? "…" : state === "recording" ? "🔴" : "🎙️"}
      {state === "error" && error && <span className="l2-icon-btn__error" role="alert">{error}</span>}
    </button>
  );
}
